if (!customElements.get('wls-contact-form')) {
  customElements.define(
    'wls-contact-form',
    class WlsContactForm extends HTMLElement {
      constructor() {
        super();
        this.isSubmitting = false;

        this.classes = {
          loading: 'loading',
          error: 'error',
          form: 'js-wls-contact-form',
          submitButton: 'wls-submit-button',
          formField: 'wls-form-field',
          formFieldError: 'wls-form-field__error',
          successMessage: 'wls-contact-form-block__message success',
          errorMessage: 'wls-contact-form-block__message error',
        };

        this.messages = {
          success: this.dataset.successMessage || 'Thank you for your message! We will get back to you soon.',
        };
      }

      get elements() {
        return {
          form: this.querySelector(`.${this.classes.form}`),
          submitButton: this.querySelector(`.${this.classes.submitButton}`),
        };
      }

      connectedCallback() {
        this.setupEventListeners();
      }

      disconnectedCallback() {
        this.removeEventListeners();
      }

      setupEventListeners() {
        const { form } = this.elements;
        if (!form) return;
        form.addEventListener('submit', this.handleFormSubmit.bind(this));
        this.setupRealTimeValidation();
      }

      removeEventListeners() {
        const { form } = this.elements;
        if (form) {
          form.removeEventListener('submit', this.handleFormSubmit.bind(this));
        }
      }

      // Setup real-time validation for inputs
      setupRealTimeValidation() {
        const { form } = this.elements;

        const inputs = form.querySelectorAll('input, textarea, select');

        inputs.forEach((input) => {
          input.addEventListener('blur', this.handleFieldBlur.bind(this));
          input.addEventListener('input', this.handleFieldInput.bind(this));

          // Special handling for checkboxes and radio buttons
          if (input.type === 'checkbox' || input.type === 'radio') {
            input.addEventListener('change', this.handleFieldInput.bind(this));
          }
        });
      }

      // Handle field blur event
      handleFieldBlur(event) {
        const field = event.target;

        // Handle different field types
        if (field.type === 'checkbox' || field.type === 'radio') {
          // For checkboxes and radio buttons, validate on blur
          this.validateField(field);
        } else if (field.hasAttribute('required') && !field.value.trim()) {
          this.showFieldError(field, 'This field is required');
        } else if (field.value.trim()) {
          // Validate field if it has content
          this.validateField(field);
        } else {
          this.clearFieldError(field);
        }
      }

      // Handle field input event
      handleFieldInput(event) {
        const field = event.target;

        // For checkboxes and radio buttons, validate immediately
        if (field.type === 'checkbox' || field.type === 'radio') {
          this.validateField(field);
        } else if (this.hasFieldError(field)) {
          this.clearFieldError(field);
        }
      }

      // Main form submission handler
      handleFormSubmit(event) {
        event.preventDefault();

        if (this.isSubmitting) return;

        // Clear previous errors
        this.clearAllErrors();

        // Validate all fields
        if (!this.validateForm()) {
          return false;
        }

        // Submit form
        // this.submitForm();
      }

      // Validate entire form
      validateForm() {
        const { form } = this.elements;
        let isValid = true;
        const requiredFields = form.querySelectorAll('[required]');

        requiredFields.forEach((field) => {
          if (!this.validateField(field)) {
            isValid = false;
          }
        });

        return isValid;
      }

      // Validate individual field
      validateField(field) {
        const { form } = this.elements;
        const fieldContainer = field.closest(`.${this.classes.formField}`);
        if (!fieldContainer) return true;

        // Clear existing error
        this.clearFieldError(field);

        // Check if required field is empty
        if (field.hasAttribute('required') && !field.value.trim()) {
          this.showFieldError(field, 'This field is required');
          return false;
        }

        // Skip validation if field is empty and not required
        if (!field.value.trim()) return true;

        // Skip validation for checkboxes and radio buttons if they're not required
        if ((field.type === 'checkbox' || field.type === 'radio') && !field.hasAttribute('required')) {
          return true;
        }

        // Skip validation for select fields if they're not required and empty
        if (field.tagName === 'SELECT' && !field.hasAttribute('required') && !field.value) {
          return true;
        }

        // Skip validation for file fields if they're not required and empty
        if (field.type === 'file' && !field.hasAttribute('required') && !field.files.length) {
          return true;
        }

        // Skip validation for date/time fields if they're not required and empty
        if (
          (field.type === 'date' || field.type === 'time' || field.type === 'datetime-local') &&
          !field.hasAttribute('required') &&
          !field.value
        ) {
          return true;
        }

        // Skip validation for number fields if they're not required and empty
        if (field.type === 'number' && !field.hasAttribute('required') && !field.value) {
          return true;
        }

        // Skip validation for URL fields if they're not required and empty
        if (field.type === 'url' && !field.hasAttribute('required') && !field.value) {
          return true;
        }

        // Email validation
        if (field.type === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(field.value.trim())) {
            this.showFieldError(field, 'Please enter a valid email address');
            return false;
          }
        }

        // Phone validation
        if (field.type === 'tel') {
          const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
          if (!phoneRegex.test(field.value.replace(/[\s\-\(\)]/g, ''))) {
            this.showFieldError(field, 'Please enter a valid phone number');
            return false;
          }
        }

        // URL validation
        if (field.type === 'url') {
          try {
            new URL(field.value);
          } catch {
            this.showFieldError(field, 'Please enter a valid URL');
            return false;
          }
        }

        // Pattern validation
        if (field.pattern && field.pattern.trim() !== '' && !new RegExp(field.pattern).test(field.value)) {
          this.showFieldError(field, 'Please enter a valid format');
          return false;
        }

        // Min/Max length validation
        if (field.minLength && field.minLength > 0 && field.value.length < field.minLength) {
          this.showFieldError(field, `Minimum length is ${field.minLength} characters`);
          return false;
        }

        if (field.maxLength && field.maxLength > 0 && field.value.length > field.maxLength) {
          this.showFieldError(field, `Maximum length is ${field.maxLength} characters`);
          return false;
        }

        // Number validation
        if (field.type === 'number') {
          const value = parseFloat(field.value);
          if (field.min && value < parseFloat(field.min)) {
            this.showFieldError(field, `Minimum value is ${field.min}`);
            return false;
          }
          if (field.max && value > parseFloat(field.max)) {
            this.showFieldError(field, `Maximum value is ${field.max}`);
            return false;
          }
        }

        // Checkbox validation (for required checkboxes)
        if (field.type === 'checkbox' && field.hasAttribute('required') && !field.checked) {
          this.showFieldError(field, 'This field is required');
          return false;
        }

        // Radio button validation (for required radio groups)
        if (field.type === 'radio' && field.hasAttribute('required')) {
          const radioGroup = form.querySelectorAll(`input[name="${field.name}"]`);
          const isChecked = Array.from(radioGroup).some((radio) => radio.checked);
          if (!isChecked) {
            this.showFieldError(field, 'Please select an option');
            return false;
          }
        }

        // Select validation (for required select fields)
        if (field.tagName === 'SELECT' && field.hasAttribute('required') && !field.value) {
          this.showFieldError(field, 'Please select an option');
          return false;
        }

        // File validation (for required file fields)
        if (field.type === 'file' && field.hasAttribute('required') && !field.files.length) {
          this.showFieldError(field, 'Please select a file');
          return false;
        }

        // Date validation (for required date fields)
        if (field.type === 'date' && field.hasAttribute('required') && !field.value) {
          this.showFieldError(field, 'Please select a date');
          return false;
        }

        // Time validation (for required time fields)
        if (field.type === 'time' && field.hasAttribute('required') && !field.value) {
          this.showFieldError(field, 'Please select a time');
          return false;
        }

        // DateTime validation (for required datetime-local fields)
        if (field.type === 'datetime-local' && field.hasAttribute('required') && !field.value) {
          this.showFieldError(field, 'Please select a date and time');
          return false;
        }

        return true;
      }

      // Show field error
      showFieldError(field, message) {
        const fieldContainer = field.closest(`.${this.classes.formField}`);
        if (!fieldContainer) return;

        // Remove existing error
        this.clearFieldError(field);

        // Add error styling
        field.classList.add(this.classes.error);

        // Create error message
        const errorDiv = document.createElement('div');
        errorDiv.className = this.classes.formFieldError;
        errorDiv.textContent = message;
        fieldContainer.appendChild(errorDiv);
      }

      // Clear field error
      clearFieldError(field) {
        const fieldContainer = field.closest(`.${this.classes.formField}`);
        if (!fieldContainer) return;

        // Remove error styling
        field.classList.remove(this.classes.error);

        // Remove error message
        const errorElement = fieldContainer.querySelector(`.${this.classes.formFieldError}`);
        if (errorElement) {
          errorElement.remove();
        }
      }

      // Check if field has error
      hasFieldError(field) {
        const fieldContainer = field.closest(`.${this.classes.formField}`);
        if (!fieldContainer) return false;
        return fieldContainer.querySelector(`.${this.classes.formFieldError}`) !== null;
      }

      // Clear all errors
      clearAllErrors() {
        const { form } = this.elements;
        const errorElements = form.querySelectorAll(`.${this.classes.formFieldError}`);
        errorElements.forEach((error) => error.remove());

        const fields = form.querySelectorAll('input, textarea, select');
        fields.forEach((field) => {
          field.style.borderColor = '';
        });
      }

      // Submit form
      async submitForm(e) {
        this.isSubmitting = true;
        this.setSubmitButtonState(true);

        try {
          const { form } = this.elements;
          const formData = new FormData(form);
          const response = await fetch(form.action, {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            this.showSuccessMessage();
            this.resetForm();
          } else {
            throw new Error('Form submission failed');
          }
        } catch (error) {
          console.error('Form submission error:', error);
          this.showErrorMessage();
        } finally {
          this.isSubmitting = false;
          this.setSubmitButtonState(false);
        }
      }

      // Set submit button state
      setSubmitButtonState(isLoading) {
        const { submitButton } = this.elements;
        if (!submitButton) return;
        submitButton.classList.toggle(this.classes.loading, isLoading);
      }

      // Show success message
      showSuccessMessage() {
        const { form } = this.elements;
        const successMessage = document.createElement('div');
        successMessage.className = this.classes.successMessage;
        successMessage.innerHTML = this.messages.success;
        form.insertBefore(successMessage, form.firstChild);
      }

      // Show error message
      showErrorMessage() {
        const { form } = this.elements;
        const errorMessage = document.createElement('div');
        errorMessage.className = this.classes.errorMessage;
        errorMessage.innerHTML = 'There was an error submitting your form. Please try again.';
        form.insertBefore(errorMessage, form.firstChild);
      }

      // Reset form
      resetForm() {
        const { form } = this.elements;
        form.reset();
        this.clearAllErrors();
      }

      // Public methods for external control

      // Validate specific field by selector
      validateFieldBySelector(selector) {
        const { form } = this.elements;
        const field = form.querySelector(selector);
        if (field) {
          return this.validateField(field);
        }
        return false;
      }

      // Clear specific field error by selector
      clearFieldErrorBySelector(selector) {
        const { form } = this.elements;
        const field = form.querySelector(selector);
        if (field) {
          this.clearFieldError(field);
        }
      }

      // Show specific field error by selector
      showFieldErrorBySelector(selector, message) {
        const { form } = this.elements;
        const field = form.querySelector(selector);
        if (field) {
          this.showFieldError(field, message);
        }
      }

      // Get form data as object
      getFormData() {
        const { form } = this.elements;
        const formData = new FormData(form);
        const data = {};
        for (let [key, value] of formData.entries()) {
          data[key] = value;
        }
        return data;
      }

      // Set form data from object
      setFormData(data) {
        const { form } = this.elements;
        Object.keys(data).forEach((key) => {
          const field = form.querySelector(`[name="contact[${key}]"]`);
          if (field) {
            field.value = data[key];
          }
        });
      }

      // Enable/disable form
      setFormEnabled(enabled) {
        const { form } = this.elements;
        const fields = form.querySelectorAll('input, textarea, select, button');
        fields.forEach((field) => {
          field.disabled = !enabled;
        });
      }

      // Focus on specific field
      focusField(selector) {
        const { form } = this.elements;
        const field = form.querySelector(selector);
        if (field) {
          field.focus();
        }
      }

      // Scroll to specific field
      scrollToField(selector) {
        const { form } = this.elements;
        const field = form.querySelector(selector);
        if (field) {
          field.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }
  );
}
